import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, EMAILJS_PUBLIC_KEY } from '../emailConfig';
import './Contact.css';

function Contact() {
  // 1. Set up form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  // 2. Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // 3. Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Same template as the booking form; trip fields are marked N/A
    const templateParams = {
      form_type: 'Contact Message',
      passenger_name: formData.name,
      phone: formData.phone || 'N/A',
      email: formData.email,
      reply_to: formData.email,
      service_type: 'N/A',
      pickup_address: 'N/A',
      dropoff_address: 'N/A',
      pickup_map_link: 'N/A',
      dropoff_map_link: 'N/A',
      pickup_date: 'N/A',
      appointment_time: 'N/A',
      pickup_time: 'N/A',
      round_trip: 'N/A',
      return_date: 'N/A',
      return_time: 'N/A',
      special_assistance: 'N/A',
      additional_info: formData.message,
      message: formData.message
    };

    emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        setSubmitStatus('success');
        // Reset form on success
        setFormData({ name: '', email: '', phone: '', message: '' });
      })
      .catch((err) => {
        console.error('FAILED...', err);
        setSubmitStatus('error');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="contact-page">
      {/* Header Section */}
      <div className="page-header">
        <div className="container">
          <h1>Contact Us</h1>
          <p>We are here to support you and ensure a seamless, reliable journey.</p>
        </div>
      </div>
      
      {/* Core Contact Info & Form Section */}
      <section className="contact-info-section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-details">
              
              {/* Clickable Phone Card */}
              <div className="contact-card">
                <div className="contact-icon">📞</div>
                <h3>Phone</h3>
                <p className="contact-value">
                  <a href="tel:+12028162344" className="contact-link">
                    (202) 816-2344
                  </a>
                </p>
                <p className="small text-muted">Call anytime—we answer whenever possible!</p>
              </div>
              
              {/* Clickable Email Card */}
              <div className="contact-card">
                <div className="contact-icon">✉️</div>
                <h3>Email</h3>
                <p className="contact-value">
                  <a href="mailto:yketema@getabsolutemobility.com" className="contact-link">
                    yketema@getabsolutemobility.com
                  </a>
                </p>
                <p className="small text-muted">Fast response within 1 hour</p>
              </div>
              
              {/* Updated: DMV Service Area Card */}
              <div className="contact-card">
                <div className="contact-icon">📍</div>
                <h3>Service Area</h3>
                <p className="contact-value">DMV Metropolitan Area</p>
                <p className="small text-muted">Fully licensed for DC, Maryland, & Northern Virginia</p>
              </div>
            </div>
            
            <div className="contact-form-container">
              <h3>Send us a message</h3>
              <form className="contact-form" onSubmit={handleSubmit}>
                <input 
                  type="text" 
                  name="name"
                  placeholder="Your Name" 
                  value={formData.name}
                  onChange={handleChange}
                  required 
                />
                <input 
                  type="email" 
                  name="email"
                  placeholder="Your Email" 
                  value={formData.email}
                  onChange={handleChange}
                  required 
                />
                <input 
                  type="tel" 
                  name="phone"
                  placeholder="Phone Number" 
                  value={formData.phone}
                  onChange={handleChange}
                />
                <textarea 
                  name="message"
                  placeholder="Your Message" 
                  rows="5" 
                  value={formData.message}
                  onChange={handleChange}
                  required
                ></textarea>
                
                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {/* Optional feedback UI */}
                {submitStatus === 'success' && (
                  <p className="status-success" style={{color: 'green', marginTop: '10px'}}>
                    Thank you! Your message has been sent successfully.
                  </p>
                )}
                {submitStatus === 'error' && (
                  <p className="status-error" style={{color: 'red', marginTop: '10px'}}>
                    Oops! Something went wrong. Please try calling us instead.
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
      
      {/* Hours of Operation Section */}
      <section className="hours-section">
        <div className="container">
          <h2>Hours of Operation</h2>
          <div className="hours-wrapper">
            <div className="hours-card standard-hours">
              <h3>Service Schedule</h3>
              <div className="hours-line">
                <span>Monday – Friday </span>
                <strong>6:00 AM – 6:00 PM</strong>
              </div>
              <div className="hours-line">
                <span>Saturday </span>
                <strong>7:00 AM – 5:00 PM</strong>
              </div>
              <div className="hours-line closed">
                <span>Sunday </span>
                <strong>Closed</strong>
              </div>
            </div>
            
            {/* Extended Care Policy Callout */}
            <div className="hours-notice-card">
              <h3>Our Family Commitment</h3>
              <p>
                Need assistance outside of our standard operating schedule or administrative hours? 
                <strong> Please feel free to call us anyway.</strong>
              </p>
              <p>
                If our team picks up the line and can safely accommodate your needs, we will do absolutely everything 
                within our power to support you and get you where you need to go.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;